import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { ApplianceType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';

type ApplianceProps = {
  appliance: ApplianceType;
  updateFn: (applianceId: string, updates: Partial<ApplianceType>) => void;
  duplicateFn: (applianceId: string) => void;
  deleteFn: (applianceId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function ApplianceComponent({
  appliance,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn
}: ApplianceProps) {
  const { defaultSizingItems, applianceItems } = useAppSelector(
    (state) => state.data
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);

  const defaultItem = appliance.custom
    ? defaultSizingItems.appliance
    : applianceItems.find((item) => item.itemId === appliance.itemId);
  const defaultValues: Partial<ApplianceType> = {
    label: defaultItem?.label,
    wattage: defaultItem?.wattage || 0,
    inUseOutput: defaultItem?.inUseOutput || 0,
    perHourUsage: defaultItem?.perHourUsage || 0
  };
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => appliance[key] !== defaultValues[key]);

  const handleUpdate = (changes: Partial<ApplianceType>) => {
    updateFn(appliance.uniqueId, {
      ...changes
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(appliance.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(appliance.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      appliance: {
        ...appliance,
        uniqueId: undefined,
        itemId: undefined,
        label: undefined,
        custom: undefined
      }
    });
  };

  return (
    <div
      key={appliance.uniqueId}
      className='flex w-full flex-col divide-y divide-gray-300 rounded-xl border-2 bg-gray-200 px-5'
      ref={animate}
    >
      <div className='flex min-h-[54px] items-center gap-5'>
        <div className='font-semibold'>
          {appliance.label}
          {appliance.custom && ' (Custom)'}
        </div>
        {appliance.custom && (
          <div className='w-[130px]'>
            <FormInput
              value={appliance.wattage}
              placeholder='Wattage'
              type='number'
              suffix='W'
              min={1}
              onChange={(value) => handleUpdate({ wattage: value })}
              required
            />
          </div>
        )}
        <div className='ml-auto flex items-center gap-2'>
          {appliance.custom && (
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
          )}
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDuplicateClick}
          >
            <DocumentDuplicateIcon className='h-5 w-5' />
            <Tooltip text='Duplicate Appliance' />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDeleteClick}
            onMouseLeave={() => setDeleteQueued(false)}
          >
            <TrashIcon className='h-5 w-5' />
            <Tooltip
              text={
                deleteQueued ? 'Click Again to Confirm' : 'Delete Appliance'
              }
            />
          </button>
        </div>
      </div>
      {showAdvancedOptions && (
        <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
          <FormInput
            value={appliance.label}
            label='Label'
            type='text'
            onChange={(value) => {
              handleUpdate({
                label: value
              });
            }}
            required
          />
          <FormInput
            value={
              appliance.inUseOutput ? appliance.inUseOutput * 100 : undefined
            }
            label='In Use Output'
            subLabel='(%)'
            type='number'
            suffix='%'
            min={1}
            max={100}
            onChange={(value) => {
              handleUpdate({
                inUseOutput: value / 100
              });
            }}
            required
          />
          <FormInput
            value={
              appliance.perHourUsage ? appliance.perHourUsage * 100 : undefined
            }
            label='Per Hour Usage'
            subLabel='(%)'
            type='number'
            suffix='%'
            min={1}
            max={100}
            onChange={(value) => {
              handleUpdate({
                perHourUsage: value / 100
              });
            }}
            required
          />
          {isModified && (
            <div className='flex w-full justify-end'>
              <button
                className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                type='button'
                onClick={setAsDefault}
              >
                <span>Set as Default</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
